// Tab Webhooks — estratto verbatim da DeveloperSettings.tsx (split Task free-win).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2, Key, Shield, Eye, EyeOff, AlertTriangle, CheckCircle2, Download, FileText, AlertCircle, Server, Globe, Send, RefreshCw, Webhook, BookOpen, Code, Wrench, Puzzle, Lock, Pencil, Search, X, ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react';
import {
  ALL_AVAILABLE_SCOPES,
  SCOPE_CATEGORIES,
  SCOPE_TO_EVENTS_MAP,
  WEBHOOK_EVENT_CATEGORIES as SHARED_EVENT_CATEGORIES,
  getAllowedEventsForScopes,
} from '@easyflow/shared';
import { format } from 'date-fns';
import { HubShell } from '@/components/common/HubShell';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient as api, getPublicBaseUrl } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import MCPToolsTab from '@/components/mcp/MCPToolsTab';
import MCPGatewayTab from '@/components/mcp/MCPGatewayTab';
import IntegrationTemplatesTab from '@/components/mcp/IntegrationTemplatesTab';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { useToast } from '@/hooks/use-toast';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';


interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  created_at: string;
  key_type?: 'public' | 'secret';
  allowed_form_ids?: string[];
  allowed_origins?: string[];
}

interface WebhookConfig {
  id: string;
  name: string;
  url: string;
  events: string[];
  headers: Record<string, string>;
  enabled: boolean;
  failed_attempts_count: number;
  circuit_breaker_until: string | null;
  api_key_id: string;
  api_key_name: string | null;
  api_key_prefix: string | null;
  api_key_scopes: string[];
  created_at: string;
  updated_at: string;
}


const eventCategories = SHARED_EVENT_CATEGORIES;

export function WebhooksTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookConfig | null>(null);
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formEvents, setFormEvents] = useState<string[]>([]);
  const [formHeaders, setFormHeaders] = useState<{ key: string; value: string }[]>([]);
  const [formApiKeyId, setFormApiKeyId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const { data: apiKeysList } = useQuery({
    queryKey: ['api-keys-list'],
    queryFn: async () => {
      const res = await api.get<{ data?: ApiKey[] } | ApiKey[]>('/api-keys');
      const d = res.data;
      return ((Array.isArray(d) ? d : d?.data) || []) as ApiKey[];
    },
  });

  const { data: webhooks, isLoading } = useQuery({
    queryKey: ['webhook-configs'],
    queryFn: async () => {
      const res = await api.get<{ data?: WebhookConfig[] } | WebhookConfig[]>('/webhook-configs');
      const d = res.data;
      return ((Array.isArray(d) ? d : d?.data) || []) as WebhookConfig[];
    },
  });

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingWebhook(null);
    setFormName('');
    setFormUrl('');
    setFormEvents([]);
    setFormHeaders([]);
    setFormApiKeyId(null);
  };

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; url: string; events: string[]; headers: Record<string, string>; api_key_id?: string | null }) => {
      const res = await api.post('/webhook-configs', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
      closeDialog();
      toast({ title: 'Webhook Created', description: 'Webhook configuration saved.' });
    },
    onError: (err: unknown) => {
      toast({ title: 'Error', description: getApiErrorMessage(err) || 'Failed to create webhook', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WebhookConfig> }) => {
      const res = await api.put(`/webhook-configs/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
      closeDialog();
      toast({ title: 'Webhook Updated' });
    },
    onError: (err: unknown) => {
      toast({ title: 'Error', description: getApiErrorMessage(err) || 'Failed to update webhook', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/webhook-configs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
      toast({ title: 'Webhook Deleted' });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      setTestingId(id);
      const res = await api.post(`/webhook-configs/${id}/test`);
      return res.data;
    },
    onSuccess: () => {
      setTestingId(null);
      toast({ title: 'Test Sent', description: 'Test webhook event delivered successfully.' });
    },
    onError: (err: unknown) => {
      setTestingId(null);
      toast({ title: 'Test Failed', description: getApiErrorMessage(err) || 'Failed to send test', variant: 'destructive' });
    },
  });

  const toggleEnabled = (wh: WebhookConfig) => {
    updateMutation.mutate({ id: wh.id, data: { enabled: !wh.enabled } });
  };

  const openCreate = () => {
    setEditingWebhook(null);
    setFormName('');
    setFormUrl('');
    setFormEvents([]);
    setFormHeaders([]);
    setFormApiKeyId(null);
    setIsDialogOpen(true);
  };

  const openEdit = (wh: WebhookConfig) => {
    setEditingWebhook(wh);
    setFormName(wh.name);
    setFormUrl(wh.url);
    setFormHeaders(Object.entries(wh.headers || {}).map(([key, value]) => ({ key, value })));
    setFormApiKeyId(wh.api_key_id || null);
    if (wh.api_key_id) {
      const key = (apiKeysList || []).find(k => k.id === wh.api_key_id);
      if (key) {
        const allowed = getAllowedEventsForScopes(key.scopes);
        const filtered = (wh.events || []).filter(e => allowed.has(e));
        const removed = (wh.events || []).filter(e => !allowed.has(e));
        setFormEvents(filtered);
        if (removed.length > 0) {
          toast({
            title: 'Attenzione',
            description: `${removed.length} eventi rimossi perché non più permessi dagli scope della API Key`,
            variant: 'destructive',
          });
        }
      } else {
        setFormEvents(wh.events || []);
      }
    } else {
      setFormEvents(wh.events || []);
    }
    setIsDialogOpen(true);
  };

  const toggleEvent = (event: string) => {
    setFormEvents((prev) => prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]);
  };

  const toggleCategory = (categoryEvents: string[]) => {
    const allSelected = categoryEvents.every((e) => formEvents.includes(e));
    if (allSelected) {
      setFormEvents((prev) => prev.filter((e) => !categoryEvents.includes(e)));
    } else {
      setFormEvents((prev) => [...new Set([...prev, ...categoryEvents])]);
    }
  };

  const addHeader = () => setFormHeaders((prev) => [...prev, { key: '', value: '' }]);
  const removeHeader = (index: number) => setFormHeaders((prev) => prev.filter((_, i) => i !== index));
  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    setFormHeaders((prev) => prev.map((h, i) => i === index ? { ...h, [field]: val } : h));
  };

  const handleSubmit = () => {
    if (!formName.trim() || !formUrl.trim()) {
      toast({ title: 'Errore di validazione', description: 'Nome e URL sono obbligatori', variant: 'destructive' });
      return;
    }
    try { new URL(formUrl); } catch {
      toast({ title: 'Errore di validazione', description: 'Formato URL non valido', variant: 'destructive' });
      return;
    }
    if (!formApiKeyId) {
      toast({ title: 'Errore di validazione', description: 'Seleziona una API Key. Ogni webhook deve essere associato a una API Key.', variant: 'destructive' });
      return;
    }
    const headers: Record<string, string> = {};
    formHeaders.forEach((h) => { if (h.key.trim()) headers[h.key.trim()] = h.value; });

    if (editingWebhook) {
      updateMutation.mutate({ id: editingWebhook.id, data: { name: formName, url: formUrl, events: formEvents, headers, api_key_id: formApiKeyId } });
    } else {
      createMutation.mutate({ name: formName, url: formUrl, events: formEvents, headers, api_key_id: formApiKeyId });
    }
  };

  const circuitBadge = (wh: WebhookConfig) => {
    if (wh.circuit_breaker_until && new Date(wh.circuit_breaker_until) > new Date()) {
      return <Badge variant="destructive">Circuit Open</Badge>;
    }
    if (wh.failed_attempts_count > 0 && wh.failed_attempts_count < 10) {
      return <Badge variant="outline" className="text-amber-600 border-amber-300">{wh.failed_attempts_count} failures</Badge>;
    }
    return <Badge variant="outline" className="text-green-600 border-green-300">Healthy</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Webhook Configurations</h3>
          <p className="text-sm text-muted-foreground">Receive real-time event notifications via HTTP webhooks</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Add Webhook</Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>API Key</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : !webhooks?.length ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No webhook configurations. Create one to start receiving events.</TableCell></TableRow>
            ) : (
              webhooks.map((wh) => (
                <TableRow key={wh.id}>
                  <TableCell className="font-medium">{wh.name}</TableCell>
                  <TableCell>
                    {wh.api_key_name ? (
                      <div className="flex items-center gap-1">
                        <Key className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{wh.api_key_name}</span>
                        {wh.api_key_prefix && <span className="text-xs text-muted-foreground font-mono">({wh.api_key_prefix}...)</span>}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-[200px] truncate" title={wh.url}>{wh.url}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(wh.events || []).slice(0, 3).map((e) => (
                        <Badge key={e} variant="secondary" className="text-xs">{e}</Badge>
                      ))}
                      {(wh.events || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">+{wh.events.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{circuitBadge(wh)}</TableCell>
                  <TableCell>
                    <Badge variant={wh.enabled ? 'default' : 'secondary'} className="cursor-pointer" onClick={() => toggleEnabled(wh)}>
                      {wh.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => testMutation.mutate(wh.id)} disabled={testingId === wh.id} title="Test webhook">
                        {testingId === wh.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(wh)} title="Edit">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => {
                        if (confirm(`Delete webhook "${wh.name}"?`)) deleteMutation.mutate(wh.id);
                      }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setIsDialogOpen(true); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Create Webhook'}</DialogTitle>
            <DialogDescription>Configure the webhook endpoint and events to subscribe to.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="wh-name">Name</Label>
              <Input id="wh-name" placeholder="e.g. CRM Notifications" value={formName} onChange={(e) => setFormName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-url">URL</Label>
              <Input id="wh-url" placeholder="https://example.com/webhooks" value={formUrl} onChange={(e) => setFormUrl(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-apikey">API Key <span className="text-destructive">*</span></Label>
              <select
                id="wh-apikey"
                value={formApiKeyId || ''}
                onChange={(e) => {
                  const newKeyId = e.target.value || null;
                  setFormApiKeyId(newKeyId);
                  if (newKeyId) {
                    const selectedKey = (apiKeysList || []).find(k => k.id === newKeyId);
                    if (selectedKey) {
                      const allowed = getAllowedEventsForScopes(selectedKey.scopes);
                      setFormEvents(prev => prev.filter(ev => allowed.has(ev)));
                    }
                  } else {
                    setFormEvents([]);
                  }
                }}
                className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${!formApiKeyId ? 'border-destructive' : 'border-input'}`}
              >
                <option value="">— Seleziona una API Key (obbligatorio) —</option>
                {(apiKeysList || []).map((k) => (
                  <option key={k.id} value={k.id}>{k.name} ({k.prefix}...) — {k.scopes.length} scope</option>
                ))}
              </select>
              {formApiKeyId && (() => {
                const selectedKey = (apiKeysList || []).find(k => k.id === formApiKeyId);
                if (!selectedKey) return null;
                return (
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs text-muted-foreground mr-1">Scope:</span>
                    {selectedKey.scopes.map(s => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                );
              })()}
              <p className="text-xs text-muted-foreground">
                {formApiKeyId ? 'Gli eventi disponibili sono filtrati in base agli scope della API Key selezionata' : 'Seleziona una API Key per visualizzare gli eventi disponibili'}
              </p>
              {!formApiKeyId && (apiKeysList || []).length === 0 && (
                <p className="text-xs text-destructive">Nessuna API Key disponibile. Crea prima una API Key nella sezione "API Keys".</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Eventi {formApiKeyId && <span className="text-xs font-normal text-muted-foreground ml-1">(filtrati per scope API Key)</span>}</Label>
              <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-3">
                {(() => {
                  if (!formApiKeyId) {
                    return (
                      <p className="text-sm text-muted-foreground text-center py-4">Seleziona una API Key per visualizzare gli eventi disponibili</p>
                    );
                  }

                  const selectedKey = (apiKeysList || []).find(k => k.id === formApiKeyId);
                  const allowedEvents = selectedKey ? getAllowedEventsForScopes(selectedKey.scopes) : new Set<string>();

                  const allCategories = eventCategories.filter(cat => cat.events.length > 0);

                  if (allCategories.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground text-center py-4">Nessun evento disponibile.</p>
                    );
                  }

                  const hasAnyAllowed = allCategories.some(cat => cat.events.some(e => allowedEvents.has(e)));
                  if (!hasAnyAllowed) {
                    return (
                      <p className="text-sm text-muted-foreground text-center py-4">La API Key selezionata non ha scope che coprono eventi webhook. Aggiungi scope alla API Key.</p>
                    );
                  }

                  return allCategories.map((cat) => {
                    const enabledEvents = cat.events.filter(e => allowedEvents.has(e));
                    return (
                    <div key={cat.name}>
                      <div className="flex items-center space-x-2 mb-1">
                        <Checkbox
                          id={`cat-${cat.name}`}
                          checked={enabledEvents.length > 0 && enabledEvents.every((e) => formEvents.includes(e))}
                          disabled={enabledEvents.length === 0}
                          onCheckedChange={() => toggleCategory(enabledEvents)}
                        />
                        <label htmlFor={`cat-${cat.name}`} className="text-sm font-semibold cursor-pointer">{cat.name}</label>
                        <span className="text-xs text-muted-foreground">({enabledEvents.length}/{cat.events.length})</span>
                      </div>
                      <div className="ml-6 grid grid-cols-2 gap-1">
                        {cat.events.map((event) => {
                          const isAllowed = allowedEvents.has(event);
                          if (isAllowed) {
                            return (
                              <div key={event} className="flex items-center space-x-2">
                                <Checkbox id={event} checked={formEvents.includes(event)} onCheckedChange={() => toggleEvent(event)} />
                                <label htmlFor={event} className="text-xs cursor-pointer font-mono">{event}</label>
                              </div>
                            );
                          }
                          return (
                            <Tooltip key={event}>
                              <TooltipTrigger asChild>
                                <div className="flex items-center space-x-2 opacity-40 cursor-not-allowed">
                                  <Checkbox id={event} disabled checked={false} />
                                  <label className="text-xs font-mono line-through">{event}</label>
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>La API Key selezionata non ha gli scope necessari per questo evento</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Custom Headers</Label>
                <Button size="sm" variant="outline" onClick={addHeader}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {formHeaders.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Header name" value={h.key} onChange={(e) => updateHeader(i, 'key', e.target.value)} className="flex-1" />
                  <Input placeholder="Value" value={h.value} onChange={(e) => updateHeader(i, 'value', e.target.value)} className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => removeHeader(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {editingWebhook ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

